// `extension-alert-client-contact-preview` — what the contact's "More" view shows: the
// companies behind the contact, where the candidate's More view shows a CV.
//
// ⚠️ THE TABLE HAS TWO IMPLEMENTATIONS AND THE FLAG PICKS ONE. With `jobCompanyInfoEnabled` the
// section is `<client-contact-current-companies-list>` — a component with hiring data, company
// descriptions and the other contacts at that company, each its own request; without it, the
// four-column table below. The tenant under test has the flag OFF, which is what every
// reference capture holds, so the list component arrives with a tenant that has the flag on.
//
// ⚠️ AND THE HEADING IS SINGULAR OR PLURAL ON THE COUNT: "Latest company" for one,
// "Latest companies" for more. It reads as a detail and is the first thing a reader compares
// against the screenshot.
import { createElement, type ReactElement } from 'react'
import type { AdditionalCompanyData, CurrentCompanyData } from '@woo-ext/api'

/**
 * `currentCompanySorter` — HIGH first, then LOW, then everything else.
 *
 * ⚠️ IT IS A PRIORITY FUNCTION, NOT A COMPARATOR, and `orderBy` sorts on what it returns. So
 * UNKNOWN and an absent likelihood both fall to 3 and keep their relative order, which is why a
 * contact whose companies are all unknown renders in the server's order rather than alphabetically.
 */
export function currentCompanyPriority(company: CurrentCompanyData): number {
  const level = (company.hiringLikelihood ?? '').toLowerCase()
  if (level === 'high') return 1
  if (level === 'low') return 2
  return 3
}

/** `{{::(company.hiringLikelihood.toLowerCase() === 'high' ? …toUpperCase() : …)}}` */
export function hiringLikelihoodText(level: string | null | undefined): string {
  const value = level ?? ''
  return value.toLowerCase() === 'high' ? value.toUpperCase() : value
}

export interface ClientContactPreviewProps {
  /** False until both the contact and its companies have answered — the spinner. */
  loaded?: boolean
  currentCompanies?: readonly CurrentCompanyData[]
  additionalCompanies?: readonly AdditionalCompanyData[]
  /**
   * `flex-basis` on the component ELEMENT — `ExtensionViewWidth.Detailed` (700) from the More
   * state's own controller.
   *
   * ⚠️ THE ELEMENT AND ITS CLASS ARE DIFFERENT BOXES, AND BOTH ARE STYLED.
   * `extension-alert-client-contact-preview { display: flex; flex: auto 0 0 }` sits on the
   * element and `.extension-alert-client-contact-preview { flex: 1 }` on the div inside it —
   * so a React tree that renders only the div is `flex: 1` inside the snippet's row and takes
   * whatever is left rather than its own 700px. At 378 that is the difference between the
   * reference's narrow ATS column with the companies card beside it and a full-width card with
   * the companies pushed out of the frame: 3.01%.
   */
  flexBasis?: number
}

export function ClientContactPreview({
  loaded = false,
  currentCompanies = [],
  additionalCompanies = [],
  flexBasis,
}: ClientContactPreviewProps): ReactElement {
  // `hasAddedByWooCompanies` — one note for the whole section, not a badge per row.
  const addedByWoo = currentCompanies.some((company) => company.addedByWoo === true)
  const sorted = [...currentCompanies].sort((a, b) => currentCompanyPriority(a) - currentCompanyPriority(b))

  return createElement(
    'extension-alert-client-contact-preview',
    flexBasis === undefined ? null : { style: { flexBasis: `${String(flexBasis)}px` } },
    <div className="extension-alert-client-contact-preview nicer-scroller mac-style">
      {loaded ? null : (
        <div className="client-contact-preview-loading">
          <i className="fa fa-spinner fa-spin" />
        </div>
      )}
      {!loaded ? null : (
        <div>
          {sorted.length === 0 ? null : (
            <div className="client-contact-current-companies highlight-section">
              <div className="snippet-section">
                <div className="snippet-section-header client-contact-current-companies-header">
                  <div className="client-contact-current-company-header-title">
                    {sorted.length > 1 ? 'Latest companies' : 'Latest company'}
                  </div>
                  <div className="client-contact-current-company-header-misc">
                    {addedByWoo ? <span className="companies-added-by-woo">Added by Woo</span> : null}
                  </div>
                </div>
                <div className="snippet-section-body client-contact-current-companies-body">
                  <div className="client-contact-current-companies-table">
                    <div className="client-contact-current-companies-titles clearfix">
                      {/* The first cell is a non-breaking space upstream, and it is load-bearing:
                          it is the column the logo sits under, and an empty div collapses. */}
                      <div className="leftmost-cell">&nbsp;</div>
                      <div className="client-contact-current-companies-cell client-contact-current-companies-title content-cell">
                        COMPANY
                      </div>
                      <div className="client-contact-current-companies-cell client-contact-current-companies-title content-cell">
                        INDUSTRIES
                      </div>
                      <div className="client-contact-current-companies-cell client-contact-current-companies-title likelihood-cell">
                        HIRING LIKELIHOOD
                      </div>
                    </div>
                    {sorted.map((company) => (
                      <div
                        className="client-contact-current-companies-row client-contact-current-companies-data-row clearfix"
                        key={`${company.name}-${company.title ?? ''}`}
                      >
                        <div className="current-companies-item-logo leftmost-cell">
                          <div className="current-companies-logo-container">
                            {company.logoUrl === undefined || company.logoUrl === null || company.logoUrl === '' ? (
                              <i className="fa fa-building" />
                            ) : (
                              <img src={company.logoUrl} alt="" />
                            )}
                          </div>
                        </div>
                        <div className="client-contact-current-companies-cell content-cell">
                          <div className="client-contact-current-company-container">
                            <div className="client-contact-main-content client-contact-company-name current-companies-truncate" title={company.name}>
                              {company.name}
                            </div>
                            {company.companyLinkedinUrl === undefined ||
                            company.companyLinkedinUrl === null ||
                            company.companyLinkedinUrl === '' ? null : (
                              <a
                                href={company.companyLinkedinUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="client-contact-current-company-linkedin"
                              >
                                <i className="fa fa-linkedin-square cursor-pointer" />
                              </a>
                            )}
                          </div>
                          {company.companySize === undefined || company.companySize === null || company.companySize === '' ? null : (
                            <div className="current-companies-metadata">{company.companySize}</div>
                          )}
                          {company.title === undefined || company.title === null || company.title === '' ? null : (
                            <div className="current-companies-metadata current-companies-truncate" title={company.title}>
                              {company.title}
                            </div>
                          )}
                        </div>
                        <div className="client-contact-current-companies-cell content-cell">
                          {/* ⚠️ "Unavailable" IS THE EMPTY STATE, and it is `!industries` only —
                              an EMPTY ARRAY joins to the empty string and renders as nothing,
                              which is what the reference shows for a company Woo has no
                              industries for. */}
                          <div className="client-contact-main-content current-companies-truncate">
                            {company.companyIndustries === undefined || company.companyIndustries === null
                              ? 'Unavailable'
                              : company.companyIndustries.join(', ')}
                          </div>
                        </div>
                        <div className="client-contact-current-companies-cell likelihood-cell">
                          {/* The 🔥 follows HIGH, after a non-breaking space that is part of the
                              interpolation rather than a margin. */}
                          <div className="client-contact-main-content">
                            {hiringLikelihoodText(company.hiringLikelihood)}
                            {' '}
                            {(company.hiringLikelihood ?? '').toLowerCase() === 'high' ? <span>🔥</span> : null}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* `extension-alert-client-contact-companies-component` — the contact's earlier
              companies. It renders only with data, and the component itself arrives with the
              screen that has some: this tenant's contacts answer `additionalCompanies: []`. */}
          {additionalCompanies.length === 0 ? null : (
            <div className="client-contact-master-profile extension-snippet-master-profile highlight-section">
              <div className="highlight-section" />
            </div>
          )}
        </div>
      )}
    </div>,
  )
}
